import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Text,
  Heading,
  Card,
  CardBody,
  CardHeader,
  useColorModeValue,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useToast,
  Input,
  Select,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Image
} from '@chakra-ui/react';
import { 
  ChevronRightIcon, 
  AddIcon, 
  SearchIcon,
  EditIcon,
  DeleteIcon,
  ViewIcon,
  ChevronDownIcon,
  CopyIcon
} from '@chakra-ui/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import apiClient from '../../../utils/apiClient';

const Templates = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterActive, setFilterActive] = useState('all');
  const [filterSystem, setFilterSystem] = useState('all');
  const [deleteTemplateId, setDeleteTemplateId] = useState(null);

  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const tenantId = user?.tenant_ids?.[0];

  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const cardBg = useColorModeValue('white', 'gray.700');

  useEffect(() => {
    if (tenantId) {
      fetchTemplates();
      fetchCategories();
    }
  }, [tenantId]);

  useEffect(() => {
    filterTemplates();
  }, [templates, searchTerm, filterCategory, filterActive, filterSystem]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      console.log('Fetching templates for tenant:', tenantId); // Debug log
      
      const response = await apiClient(`/templates/${tenantId}`);
      console.log('API Response:', response); // Debug log
      
      // Handle service response structure
      if (response && response.success && Array.isArray(response.data)) {
        setTemplates(response.data);
        console.log('Templates set:', response.data); // Debug log
      } else if (response && Array.isArray(response)) {
        // Fallback for direct array response
        setTemplates(response);
        console.log('Templates set (direct array):', response); // Debug log
      } else {
        console.log('No templates found or invalid response structure'); // Debug log
        setTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast({
        title: 'Error loading templates',
        description: error.message || 'Failed to load templates',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await apiClient(`/templates/${tenantId}/categories`);
      if (response && response.success && Array.isArray(response.data)) {
        setCategories(response.data);
      } else if (response && Array.isArray(response)) {
        setCategories(response);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Don't show error toast for categories as it's not critical
    }
  };

  const filterTemplates = () => {
    let filtered = templates;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(template => 
        template.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        template.category?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    if (filterCategory !== 'all') {
      filtered = filtered.filter(template => template.category === filterCategory);
    }

    // Active filter
    if (filterActive !== 'all') {
      const isActive = filterActive === 'active';
      filtered = filtered.filter(template => template.is_active === isActive);
    }

    // System filter
    if (filterSystem !== 'all') {
      const isSystem = filterSystem === 'system';
      filtered = filtered.filter(template => template.is_system === isSystem);
    }

    setFilteredTemplates(filtered);
  };

  const handleDeleteTemplate = async () => {
    if (!deleteTemplateId) return;

    try {
      const response = await apiClient(`/templates/${tenantId}/${deleteTemplateId}`, {
        method: 'DELETE'
      });

      // Handle service response structure
      if (response && response.success) {
        toast({
          title: 'Template deleted successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // Remove from local state
        setTemplates(templates.filter(template => template.id !== deleteTemplateId));
      } else if (response && response.error) {
        throw new Error(response.error);
      } else {
        throw new Error('Failed to delete template');
      }
      
    } catch (error) {
      console.error('Error deleting template:', error);
      toast({
        title: 'Error deleting template',
        description: error.message || 'Failed to delete template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setDeleteTemplateId(null);
      onDeleteClose();
    }
  };

  const handleCloneTemplate = async (templateId, templateName) => {
    try {
      const newName = `${templateName} (Copy)`;
      const response = await apiClient(`/templates/${tenantId}/${templateId}/clone`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name: newName })
      });

      if (response && response.success) {
        toast({
          title: 'Template cloned successfully',
          description: `Created "${newName}"`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        
        // Refresh templates list
        fetchTemplates();
      } else {
        throw new Error(response?.error || 'Failed to clone template');
      }
    } catch (error) {
      console.error('Error cloning template:', error);
      toast({
        title: 'Error cloning template',
        description: error.message || 'Failed to clone template',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const openDeleteModal = (templateId) => {
    setDeleteTemplateId(templateId);
    onDeleteOpen();
  };

  const getCategoryColor = (category) => {
    if (!category) return 'gray';
    
    const colors = ['blue', 'green', 'purple', 'orange', 'pink', 'teal', 'cyan'];
    const index = category.length % colors.length;
    return colors[index];
  };

  const getTemplateIcon = (template) => {
    if (template.is_system) return '🏛️';
    if (template.category) {
      switch (template.category.toLowerCase()) {
        case 'page': return '📄';
        case 'component': return '🧩';
        case 'layout': return '🎨';
        case 'email': return '📧';
        case 'blog': return '📝';
        case 'landing': return '🚀';
        default: return '📄';
      }
    }
    return '📄';
  };

  if (loading) {
    return (
      <Center h="50vh">
        <VStack spacing={4}>
          <Spinner size="lg" />
          <Text>Loading templates...</Text>
        </VStack>
      </Center>
    );
  }

  if (!tenantId) {
    return (
      <Container maxW="container.lg" py={6}>
        <Alert status="error">
          <AlertIcon />
          No tenant ID found. Please log in again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxW="container.xl" py={6}>
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink as={RouterLink} to="/">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink>Templates</BreadcrumbLink>
        </BreadcrumbItem>
      </Breadcrumb>

      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg" mb={2}>Page Templates</Heading>
            <Text color="gray.500">
              Manage your page templates and layouts
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={() => navigate('/templates/create')}
          >
            Create Template
          </Button>
        </HStack>

        {/* Filters */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody>
            <HStack spacing={4} wrap="wrap">
              <Box minW="250px">
                <HStack>
                  <SearchIcon color="gray.400" />
                  <Input
                    placeholder="Search templates..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    variant="filled"
                  />
                </HStack>
              </Box>
              
              <Select
                value={filterCategory}
                onChange={(e) => setFilterCategory(e.target.value)}
                maxW="200px"
                variant="filled"
              >
                <option value="all">All Categories</option>
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </Select>

              <Select
                value={filterActive}
                onChange={(e) => setFilterActive(e.target.value)}
                maxW="200px"
                variant="filled"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </Select>

              <Select
                value={filterSystem}
                onChange={(e) => setFilterSystem(e.target.value)}
                maxW="200px"
                variant="filled"
              >
                <option value="all">All Types</option>
                <option value="system">System</option>
                <option value="user">User Created</option>
              </Select>

              <Text fontSize="sm" color="gray.500" ml="auto">
                {filteredTemplates.length} of {templates.length} templates
              </Text>
            </HStack>
          </CardBody>
        </Card>

        {/* Templates Table */}
        <Card bg={cardBg} borderColor={borderColor} variant="outline">
          <CardBody p={0}>
            {filteredTemplates.length === 0 ? (
              <Center py={10}>
                <VStack spacing={4}>
                  <Text fontSize="lg" color="gray.500">
                    {templates.length === 0 ? 'No templates found' : 'No templates match your filters'}
                  </Text>
                  {templates.length === 0 && (
                    <Button
                      leftIcon={<AddIcon />}
                      colorScheme="blue"
                      onClick={() => navigate('/templates/create')}
                    >
                      Create Your First Template
                    </Button>
                  )}
                </VStack>
              </Center>
            ) : (
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Template</Th>
                    <Th>Category</Th>
                    <Th>Description</Th>
                    <Th>Status</Th>
                    <Th>Type</Th>
                    <Th>Created</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredTemplates.map((template) => (
                    <Tr key={template.id}>
                      <Td>
                        <VStack align="start" spacing={1}>
                          <HStack>
                            <Text fontSize="sm">{getTemplateIcon(template)}</Text>
                            <Text fontWeight="medium">{template.name}</Text>
                            {template.preview_image && (
                              <Image
                                src={template.preview_image}
                                alt="Preview"
                                boxSize="20px"
                                borderRadius="sm"
                              />
                            )}
                          </HStack>
                          <Text fontSize="xs" color="gray.500">
                            ID: {template.id}
                          </Text>
                        </VStack>
                      </Td>
                      <Td>
                        {template.category ? (
                          <Badge colorScheme={getCategoryColor(template.category)}>
                            {template.category}
                          </Badge>
                        ) : (
                          <Text fontSize="sm" color="gray.400">No category</Text>
                        )}
                      </Td>
                      <Td>
                        <Text fontSize="sm" color="gray.600" noOfLines={2} maxW="200px">
                          {template.description || 'No description'}
                        </Text>
                      </Td>
                      <Td>
                        <Badge colorScheme={template.is_active ? 'green' : 'gray'}>
                          {template.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge colorScheme={template.is_system ? 'purple' : 'blue'}>
                          {template.is_system ? 'System' : 'User'}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontSize="sm">
                          {new Date(template.created_at).toLocaleDateString()}
                        </Text>
                      </Td>
                      <Td>
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<ChevronDownIcon />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem
                              icon={<ViewIcon />}
                              onClick={() => navigate(`/templates/${template.id}`)}
                            >
                              View Details
                            </MenuItem>
                            <MenuItem
                              icon={<EditIcon />}
                              onClick={() => navigate(`/templates/${template.id}/edit`)}
                              isDisabled={template.is_system}
                            >
                              Edit Template
                            </MenuItem>
                            <MenuItem
                              icon={<CopyIcon />}
                              onClick={() => handleCloneTemplate(template.id, template.name)}
                            >
                              Clone Template
                            </MenuItem>
                            <MenuItem
                              icon={<DeleteIcon />}
                              color="red.500"
                              onClick={() => openDeleteModal(template.id)}
                              isDisabled={template.is_system}
                            >
                              Delete Template
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Delete Template</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text>
              Are you sure you want to delete this template? This action cannot be undone.
              If this template is being used by pages, the deletion will fail.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              Cancel
            </Button>
            <Button colorScheme="red" onClick={handleDeleteTemplate}>
              Delete
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default Templates;